import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaCheckCircle, FaMapMarkerAlt, FaStar } from "react-icons/fa";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { parseCompareRatingValue } from "../../utils/marketplaceProductCompare.util";
import MarketplaceAddToCartButton from "./MarketplaceAddToCartButton";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

function resolveBadge(item) {
  if (item.badge === "CERTIFIED" || item.verified) {
    return { label: "CERTIFIED", className: "marketplace-product-row__badge--certified" };
  }
  if (item.stock_label && Number(item.stock_label) > 0) {
    return { label: "IN STOCK", className: "marketplace-product-row__badge--seller" };
  }
  return null;
}

function buildCategoryTrail(item) {
  return [item.category_name, item.sub_category_name, item.sub_sub_category_name].filter(Boolean);
}

const SPEC_SLOT_COUNT = 4;

function buildSpecItems(items) {
  return items.slice(0, SPEC_SLOT_COUNT);
}

function RatingStars({ item }) {
  const rating = parseCompareRatingValue(item);
  const label = item.rating_label;

  if (rating == null && !label) return null;

  return (
    <div className="marketplace-product-row__rating">
      {rating != null ? (
        <span className="marketplace-product-row__stars" aria-hidden>
          {Array.from({ length: 5 }).map((_, index) => (
            <FaStar key={index} className={index + 1 <= Math.round(rating) ? "is-filled" : ""} />
          ))}
        </span>
      ) : null}
      {label ? <span className="marketplace-product-row__rating-label">{label}</span> : null}
    </div>
  );
}

export default function MarketplaceProductListRow({
  item,
  onToggleCompare,
  compared = false,
}) {
  const detailHref = item.details_href || `/marketplace/products/${item.id}`;
  const detailItems = buildSpecItems(Array.isArray(item.spec_items) ? item.spec_items : []);
  const certBadges = Array.isArray(item.cert_badges) ? item.cert_badges : [];
  const badge = resolveBadge(item);
  const categoryTrail = buildCategoryTrail(item);
  const hasMeta = item.stock_label != null || certBadges.length > 0;

  return (
    <article className="marketplace-product-row group">
      <div className="marketplace-product-row__media">
        {badge ? (
          <span className={`marketplace-product-row__badge ${badge.className}`}>{badge.label}</span>
        ) : null}
        <Link to={detailHref} className="marketplace-product-row__media-link">
          {item.image_path ? (
            <img src={resolveHubImage(item.image_path)} alt={item.title} loading="lazy" />
          ) : (
            <div className="marketplace-product-row__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-row__body">
        <div className="marketplace-product-row__head">
          <div className="marketplace-product-row__head-left">
            <Link to={detailHref} className="marketplace-product-row__title">
              {item.title}
            </Link>

            {categoryTrail.length ? (
              <p className="marketplace-product-row__category-trail">{categoryTrail.join(" › ")}</p>
            ) : null}

            <div className="marketplace-product-row__seller">
              {item.company_name ? <span className="marketplace-product-row__company">{item.company_name}</span> : null}
              {item.verified ? (
                <span className="marketplace-product-row__verified">
                  <FaCheckCircle aria-hidden /> VERIFIED
                </span>
              ) : null}
              {item.location ? (
                <span className="marketplace-product-row__location">
                  <FaMapMarkerAlt aria-hidden /> {item.location}
                </span>
              ) : null}
            </div>
          </div>

          <div className="marketplace-product-row__head-right">
            {item.price_label ? <p className="marketplace-product-row__price">{item.price_label}</p> : null}
            <RatingStars item={item} />
          </div>
        </div>

        {detailItems.length ? (
          <dl
            className="marketplace-product-row__specs"
            style={{ gridTemplateColumns: `repeat(${detailItems.length}, minmax(0, 1fr))` }}
          >
            {detailItems.map((spec) => (
              <div key={`${spec.label}-${spec.value}`} className="marketplace-product-row__spec">
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className={`marketplace-product-row__meta${hasMeta ? "" : " marketplace-product-row__meta--empty"}`}>
          {item.stock_label != null ? (
            <span className="marketplace-product-row__meta-chip">
              <span className="marketplace-product-row__meta-chip-label">Stock</span>
              <span className="marketplace-product-row__meta-chip-value">{item.stock_label}</span>
            </span>
          ) : null}
          {certBadges.map((cert) => (
            <span key={cert} className="marketplace-product-row__cert">
              {cert}
            </span>
          ))}
        </div>

        <div className="marketplace-product-row__actions">
          <MarketplaceAddToCartButton
            item={item}
            className="marketplace-product-row__btn marketplace-product-row__btn--outline-dark"
          />
          <Link to={detailHref} className="marketplace-product-row__btn marketplace-product-row__btn--primary">
            Request for Quote
          </Link>
          <Link
            to={item.company_id ? `/verified-suppliers?company=${item.company_id}` : "/verified-suppliers"}
            className="marketplace-product-row__btn marketplace-product-row__btn--outline-dark"
          >
            Contact Supplier
          </Link>
          <button
            type="button"
            className={`marketplace-product-row__btn marketplace-product-row__btn--outline${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(item.id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={item.id}
            title={item.title}
            sharePath={detailHref}
            imagePath={item.image_path}
            companyName={item.company_name}
            description={item.short_description || item.description}
            triggerClassName="marketplace-product-row__btn marketplace-product-row__btn--outline marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
